import { IFormValues, IFormErrors } from './use-form'
import { getStickyHeaderHeight } from '../lib/get-sticky-header-height'
import { Currency } from '../../shared/lib/currency'
import { isBrowser } from '@scentregroup/shared/env'

export enum TermsAndConditionslocation {
  CART = '/shop/westfield-digital-gift-card/cart',
  CHECKOUT = '/shop/westfield-digital-gift-card/checkout',
}

export type ITermsAndConditions = [
  string,
  TermsAndConditionslocation | undefined,
]

export const checkIfTouched =
  <T>(values: IFormValues<T>, isSubmitting?: boolean) =>
  (key: string) => {
    return isSubmitting || key in values
  }

export const isTermsAndConditionsTick = (
  cartId: string | undefined,
  termsAndConditions: ITermsAndConditions
): boolean => !!cartId && termsAndConditions[0] === cartId

const scrollIntoView = (id: string): void => {
  if (isBrowser()) {
    const element = document.getElementById(id)
    if (element && typeof window.scrollTo === 'function') {
      const top = element.offsetTop - getStickyHeaderHeight()
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }
}

type Keys<T> = (keyof T)[]

export const scrollToFirstError = <T extends IFormErrors<T>>(
  errors: T,
  scrollToMappings: { [key in keyof T]: string },
  scrollFunction: (id: string) => void = scrollIntoView
): void => {
  const errorKeys = Object.keys(errors) as Keys<T>

  for (let i = 0; i < errorKeys.length; i++) {
    const error = errorKeys[i] as keyof T
    if (errors[error]) {
      scrollFunction(scrollToMappings[error])
      break
    }
  }
}

const getActiveElement = (): HTMLElement | undefined =>
  isBrowser() ? (document.activeElement as HTMLElement | undefined) : undefined

export const blurActiveAndGetFinalValues = <T extends { [key: string]: any }>(
  values: IFormValues<T>,
  getActiveElementFn = getActiveElement
): T => {
  const activeElement = getActiveElementFn()

  let valuesOnSubmit = { ...values }

  if (activeElement && typeof activeElement.blur === 'function') {
    activeElement?.blur()

    // exclude delivery date from active element checking as the date should be taken from the calendar and not via input
    if (
      !!activeElement.id &&
      !!activeElement.getAttribute('value') &&
      activeElement.id !== 'deliveryDate'
    ) {
      valuesOnSubmit = {
        ...values,
        [activeElement.id]:
          activeElement.id === 'amount'
            ? new Currency(activeElement.getAttribute('value')!)
            : activeElement.getAttribute('value'),
      }
    }
  }

  return valuesOnSubmit as T
}
